import { z } from 'zod';

/**
 * Transactional (trading) push of a campaign (BB-78): signals, price alerts, stop loss, take profit, margin calls.
 * A push of an event older than `maxEventAgeSeconds` is not sent (stale), and never shown after
 * `occurredAt + maxEventAgeSeconds`; `dedupe` keeps one push per key and profile within its window (a margin call
 * repeated by the backend). Transactional campaigns bypass every marketing rule but never consent: an unsubscribed
 * push channel or subscription group still stops them.
 */
export const TransactionalSettings = z.strictObject({
  maxEventAgeSeconds: z.int().min(5).max(86_400),
  dedupe: z.strictObject({ keyTemplate: z.string().min(1).max(512), windowSeconds: z.int().min(1).max(86_400) }).optional(),
  subscriptionGroupId: z.uuid().nullable().default(null),
  /** Why the campaign is sent without a subscription group (required when there is none). */
  noGroupJustification: z.string().trim().min(10).max(500).optional(),
});
export type TransactionalSettings = z.infer<typeof TransactionalSettings>;

export const TRANSACTIONAL_SUPPRESS_REASONS = ['stale_event', 'dedupe_window'] as const;

export interface TransactionalIssue {
  code: string;
  path: string;
  message: string;
}

/**
 * Speed of a transactional campaign over the last `windowHours` (BB-78 SLA, P95 < 5 s): from the event reaching the
 * service to the provider request of its push. `null` percentiles until something was sent in the window.
 */
export interface TransactionalLatency {
  windowHours: number;
  sends: number;
  p50Ms: number | null;
  p95Ms: number | null;
  /** Pushes not sent in the window, by reason. */
  suppressed: Record<(typeof TRANSACTIONAL_SUPPRESS_REASONS)[number], number>;
}
